import { generateJSON } from '@tiptap/html';
import { isEmpty, last } from 'lodash';

import { SINGLE_EDITOR_TASK_LIST_STATIC_TIPTAP_EXTENSIONS } from '../tiptap/extensions/taskList/taskListExtensionLists';
import { TiptapContent, TiptapContentNode, TiptapNodeType } from '../tiptap/tiptapTypes';

// NOTE: Unlike the rest of `src/common/taskLists/`, this module is not server-safe - its extension
//  list transitively imports React and SCSS. Keep it off server code paths.

/**
 * The task list content found in clipboard HTML, ready to be built into a task list element.
 */
export type TaskListClipboardContent = {
    /** The list's title node, when the copy began with a title or a heading. */
    title?: TiptapContentNode;
    /** The list's top level task item nodes, in document order. */
    taskItems: TiptapContentNode[];
    /**
     * Whether the HTML is provably a copy from a single-editor task list, rather than from
     * something that merely looks like one (a legacy checklist in a card serialises to the same
     * `ul`/`li` markup). Proven by a `taskId` on a task item or by the list's own title node.
     * A copy of a single-editor list with neither is indistinguishable, so `false` means
     * unproven, not disproven.
     */
    isProvenSingleEditorOrigin: boolean;
};

/**
 * Parses clipboard HTML into the single-editor task list schema, returning nothing when the
 * HTML is empty or cannot be parsed at all.
 */
const parseClipboardDocument = (html: string): TiptapContent | null => {
    if (!html) return null;

    try {
        return generateJSON(html, SINGLE_EDITOR_TASK_LIST_STATIC_TIPTAP_EXTENSIONS) as TiptapContent;
    } catch {
        return null;
    }
};

/**
 * Converts a leading heading or title node into the list's title node.
 *
 * `TaskListTitle` is plain text only (`(text | hardBreak)*` with no marks), so a heading's styling
 * and any inline nodes within it are dropped rather than carried into an unrepresentable title.
 */
const createTaskListTitle = (node: TiptapContentNode): TiptapContentNode => {
    const content: TiptapContentNode[] = [];

    (node.content ?? []).forEach((child) => {
        if (child.type === TiptapNodeType.hardBreak) {
            content.push({ type: TiptapNodeType.hardBreak });
            return;
        }

        if (child.type !== TiptapNodeType.text) return;

        // Dropping the marks can leave a run of adjacent text nodes, which the title holds as one.
        const previous = last(content);

        if (previous?.type === TiptapNodeType.text) previous.text = `${previous.text}${child.text}`;
        else content.push({ type: TiptapNodeType.text, text: child.text });
    });

    return isEmpty(content) ? { type: TiptapNodeType.taskListTitle } : { type: TiptapNodeType.taskListTitle, content };
};

/**
 * Converts a leading paragraph into a task item, so the first line of a copy that started
 * mid-task is not lost.
 */
const createTaskItemFromParagraph = (paragraph: TiptapContentNode): TiptapContentNode => ({
    type: TiptapNodeType.taskItem,
    attrs: { checked: false },
    content: [paragraph],
});

/**
 * Whether a node or anything beneath it is a heading.
 *
 * Headings are only in the parse schema so a leading one can become the title; the editor has no
 * heading node, so one surviving anywhere else means the content is not a task list copy.
 */
const containsHeading = (node: TiptapContentNode): boolean =>
    node.type === TiptapNodeType.heading || !!node.content?.some(containsHeading);

/**
 * Whether a task item, or any sub-task nested beneath it, carries a `taskId`.
 *
 * Only the single editor's `UniqueID` extension writes one, so finding one anywhere proves the
 * copy came from there.
 */
const containsTaskId = (node: TiptapContentNode): boolean =>
    !!node.attrs?.taskId || !!node.content?.some(containsTaskId);

/**
 * Reads the task list content out of clipboard HTML, returning nothing when the content does not
 * qualify as a task list and should be pasted by the existing card path instead.
 *
 * Content qualifies when it is an optional leading title or heading, an optional leading
 * paragraph, and otherwise only task lists. A heading and a paragraph together are both allowed:
 * that is a copy that started at the list's title and ran into the middle of its first task.
 */
export const readTaskListClipboardContent = (html: string): TaskListClipboardContent | null => {
    const documentNodes = parseClipboardDocument(html)?.content;

    if (!documentNodes?.length) return null;

    const [leadingNode, ...nodesAfterLeading] = documentNodes;

    const isTitleNode = leadingNode.type === TiptapNodeType.taskListTitle;
    const hasTitle = isTitleNode || leadingNode.type === TiptapNodeType.heading;
    const nodesAfterTitle = hasTitle ? nodesAfterLeading : documentNodes;

    const leadingParagraph = nodesAfterTitle[0]?.type === TiptapNodeType.paragraph ? nodesAfterTitle[0] : undefined;
    const taskListNodes = leadingParagraph ? nodesAfterTitle.slice(1) : nodesAfterTitle;

    if (isEmpty(taskListNodes)) return null;
    if (taskListNodes.some((node) => node.type !== TiptapNodeType.taskList)) return null;
    if (taskListNodes.some(containsHeading)) return null;

    const taskItems = [
        ...(leadingParagraph ? [createTaskItemFromParagraph(leadingParagraph)] : []),
        ...taskListNodes.flatMap((taskList) => taskList.content ?? []),
    ];

    return {
        ...(hasTitle ? { title: createTaskListTitle(leadingNode) } : {}),
        taskItems,
        isProvenSingleEditorOrigin: isTitleNode || taskItems.some(containsTaskId),
    };
};
